import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Flex } from 'components';
import { SendFeedbackProps, SidebarContentsList, useContents } from 'features/contents';
import Chat from 'features/chat';
import Timeline, { TimelinePointState } from 'features/timeline';
import Comments, { useComments } from 'features/comments';
import { Breakpoints } from 'app/hooks';
import { useProducts } from 'features/products';
import { useSchool } from 'features/school';

interface WindowSize {
  innerWidth: number;
  innerHeight: number;
  videoWidth: number;
  videoHeight: number;
}

interface TimelineProps {
  timelinePoint: TimelinePointState;
  playerGoTo: (seconds: number) => void;
  sendFeedback: (props: SendFeedbackProps) => void;
  breakpoint: Breakpoints;
  orientation: 'portrait' | 'landscape';
  windowSize: WindowSize;
}

const TimelineWrapper = styled(Flex)`
  position: relative;

  &::before {
    content: '';
    width: 100%;
    height: 40px;
    position: absolute;
    z-index: 10;
    bottom: 0;
    pointer-events: none;

    ${(props: any) => {
      if (props['data-shadow']) {
        return `
          background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, ${props.theme.colors.videoTimelineActiveDegrade} 100%);
        `;
      }
    }}
  }
`;

const TabButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 50%;
  border: 0;
  background: transparent;
  color: white;
  flex-grow: 1;
  position: relative;

  cursor: pointer;
  text-transform: uppercase;
  font-weight: 400;
  outline: none;

  span {
    display: flex;
    align-items: center;
    background: ${({ theme }) => theme.colors.black30};
    font-size: 10px;
    margin-left: 10px;
    padding: 0 5px;
    border-radius: 4px;
  }

  &::before {
    content: '';
    display: block;
    background: ${(props: any) => (props['data-active'] ? props.theme.colors.primary_400 : 'transparent')};
    width: 60%;
    height: 3px;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const CommentsWrapper = styled(Flex)`
  width: 100vw;
  height: ${(props: any) => `calc(100vh - ${props['data-videoheight'] + (props['data-hastabs'] ? 167 : 122)}px)`};
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const VideoTimeline = ({ timelinePoint, playerGoTo, sendFeedback, orientation, windowSize, breakpoint }: TimelineProps) => {
  const { contentDetails } = useContents();
  const {
    fetchCommentsConfig,
    commentsConfig: { enableComments },
  } = useComments();
  const { productEvolutionDetails } = useProducts();
  const { schoolAlias } = useSchool();

  const hasTimeline = Boolean(contentDetails?.video?.timeline.length);

  const hasDetails = Boolean(contentDetails?.name || contentDetails?.description || !!contentDetails?.downloads?.length);

  const hasChat = !!contentDetails?.video?.videoChatProvider;

  const hasComments = !!enableComments && breakpoint !== 'desktop';

  const hasContentsList = !!productEvolutionDetails?.timelines?.length || !!productEvolutionDetails?.items?.length;

  const hasTabs = ((hasDetails || hasTimeline) && (hasContentsList || hasComments || hasChat)) || false;

  let initialCurrentTab = 'timeline';

  // if (!hasTimeline && hasDetails) {
  //   initialCurrentTab = 'details';
  // }

  // initialCurrentTab = 'contents';

  const [currentTab, setCurrentTab] = useState(initialCurrentTab);

  useEffect(() => {
    if (enableComments === null) {
      fetchCommentsConfig();
    }

    if (breakpoint === 'desktop' && currentTab === 'comments') {
      setCurrentTab('timeline');
    }
  }, [breakpoint, enableComments, currentTab]);

  if (!contentDetails?.video) return <></>;

  return (
    <>
      {Boolean(hasTimeline || hasDetails || hasChat || hasComments || hasContentsList) && (
        <TimelineWrapper
          data-shadow={currentTab !== 'contents'}
          flexGrow={0}
          flexShrink={0}
          width={{ m: '100%', d: 425 }}
          bg="videoTimelineBg"
          flexDirection="column"
          flexWrap="nowrap"
        >
          {hasTabs && (
            <Flex
              maxHeight={{ m: 45, d: 60 }}
              minHeight={{ m: 45, d: 60 }}
              flexShrink={0}
              bg={schoolAlias === 'b3' ? 'dark_150' : 'gray_600'}
              justifyContent="flex-start"
            >
              {(hasTimeline || hasDetails) && (
                <TabButton data-active={currentTab === 'timeline'} onClick={() => setCurrentTab('timeline')}>
                  Detalhes
                </TabButton>
              )}

              {hasComments && (
                <TabButton data-active={currentTab === 'comments'} onClick={() => setCurrentTab('comments')}>
                  Comentários
                </TabButton>
              )}

              {hasChat && (
                <TabButton data-active={currentTab === 'chat'} onClick={() => setCurrentTab('chat')}>
                  Chat
                </TabButton>
              )}

              {hasContentsList && (
                <TabButton data-active={currentTab === 'contents'} onClick={() => setCurrentTab('contents')}>
                  Aulas
                </TabButton>
              )}
            </Flex>
          )}

          {(hasTimeline || hasDetails) && (currentTab === 'timeline' || currentTab === 'details') && (
            <Timeline
              timelinePoint={timelinePoint}
              playerGoTo={playerGoTo}
              sendFeedback={sendFeedback}
              orientation={orientation}
              hasTabs={hasTabs}
              windowSize={windowSize}
            />
          )}

          {hasChat && currentTab === 'chat' && (
            <Chat
              videoChatProvider={contentDetails?.video.videoChatProvider}
              externalVideoChatProviderIdOrUrl={contentDetails?.video.externalVideoChatProviderIdOrUrl}
              hasTabs={hasTabs}
            />
          )}

          {enableComments && currentTab === 'comments' && (
            <CommentsWrapper data-hastabs={hasTabs} data-videoheight={windowSize.videoHeight}>
              <Comments />
            </CommentsWrapper>
          )}

          {hasContentsList && currentTab === 'contents' && (
            <SidebarContentsList orientation={orientation} hasTabs={hasTabs} windowSize={windowSize} />
          )}
        </TimelineWrapper>
      )}
    </>
  );
};

export default VideoTimeline;
